<script setup lang="ts">
//#region import
import { onBeforeUnmount, onMounted, computed, ref } from 'vue';
import { cloneDeep } from 'lodash-es';
import { useI18n } from 'vue-i18n';
import { usePopupManager } from '@/components/PopupManager.vue';
import eventBus from '@/setup/eventBus';
import { useApi } from '@/api';
import { useTypedStore } from '@/store';
import type { UiTableNewField } from '@/types/table';
import type { GetSessionsResponse } from '@/types/openapi-ts';
import type { TimeOption } from '@/components/Popups/EditSignificantTtlPopup.vue';
//#endregion

//#region const
const fields: UiTableNewField<GetSessionsResponse['payload'][number]>[] = [
  {
    name: 'device_model',
    align: 'left',
    minWidth: '132',
  },
  {
    name: 'os',
    align: 'left',
    minWidth: '132',
    renderValue: (field, row) => {
      if (field || row.browser) {
        return `${field}/${row.browser}`;
      }
      return 'unknown';
    },
  },
  {
    name: 'user_ip',
    align: 'left',
    width: '125',
    minWidth: '125',
    format: 'ip-flag',
  },
  {
    name: 'last_active',
    width: '150',
    minWidth: '150',
    align: 'left',
    headerAlign: 'left',
    format: 'date',
    formatOptions: {
      in: '',
      out: 'dd MMM yyyy HH:mm',
    },
  },
  {
    name: 'actions',
    width: '80',
    align: 'center',
    headerAlign: 'center',
    controlType: 'icon-action',
    controlColorIco: () => 'var(--text-negative)',
    action: (row) => handleTerminate(row),
    actionName: 'times',
    tooltip: 'profile.terminate',
  },
];
//#endregion

//#region composables
const { t } = useI18n();
const store = useTypedStore();
const api = useApi();
const popupManager = usePopupManager();
//#endregion

//#region state
const tableData = ref<GetSessionsResponse['payload']>([]);
const isDataLoading = ref(false);
const isFirstLoading = ref(true);
const errors = ref({});
//#endregion

//#region computed
const adminInfo = computed(() => store.getters['auth/adminInfo']);
const timezones = computed(() => store.getters['misc/timezones']);

const significantActionsPeriodName = computed(() => {
  return significantActionsPeriodList.value
    .find(({ value }) => value === adminInfo.value.significant_actions_ttl)?.label;
});

const profileTimezone = computed(() => {
  if (adminInfo.value.timezone && timezones.value) {
    const timezone = timezones.value.filter((item) => item.zone.includes(adminInfo.value.timezone));
    return timezone[0] ? timezone[0].name : t('profile.tz_invalid');
  }
  return t('profile.tz_invalid');
});

const isDisabledTooltipName = computed(() => {
  return adminInfo.value.name.length < 20;
});

const significantActionsPeriodList = computed<TimeOption[]>(() => {
  return [
    {
      value: 30 * 60,
      label: t('affiliates.time.minutes', { count: 30 }, 30),
    },
    ...[1, 2, 4, 8, 12, 24].reduce((acc, count) => acc.concat({
      value: count * 60 * 60,
      label: t('affiliates.time.hours', { count }, count),
    }), [] as TimeOption[]),
  ];
});
//#endregion

//#region methods
const checkSensitiveAction = (action: string, row?: GetSessionsResponse['payload'][number]) => {
  eventBus.$emit('checkSensitiveAction', action, row);
};

const handleUpdatePassword = () => {
  popupManager.editPassword.open({});
};

const handleEditSignificantTtl = () => {
  popupManager.editSignificantTtl.open({
    data: {
      significantPeriodList: significantActionsPeriodList.value,
    },
  });
};

const handleEditProfile = () => {
  popupManager.editProfile.open({});
};

const handleTerminate = (row: GetSessionsResponse['payload'][number]) => {
  const confirmPopup = popupManager.confirm.open({
    data: {
      title: t('profile_popup.terminate_title'),
      message: t('profile_popup.terminate_msg'),
      bodyIcon: 'exclamation-triangle',
      bodyIconColor: 'var(--text-negative)',
      confirmTitle: t('profile_popup.terminate_action'),
      confirmType: 'negative',
    },
    listeners: {
      success: async () => {
        const context = await confirmPopup;
        try {
          await api.deleteSession(row.id);
          getSessions();
          context.handleClose();
        } catch (error) {
          window.console.error(error);
        }
      },
    },
  });
};

const handleTerminateAll = (row: GetSessionsResponse['payload'][number]) => {
  const confirmPopup = popupManager.confirm.open({
    data: {
      title: t('profile_popup.terminate_title_all'),
      message: t('profile_popup.terminate_msg_all'),
      bodyIcon: 'exclamation-triangle',
      bodyIconColor: 'var(--text-negative)',
      confirmTitle: t('profile_popup.terminate_action_all'),
      confirmType: 'negative',
    },
    listeners: {
      success: async () => {
        const context = await confirmPopup;
        try {
          await api.deleteSessions();
          getSessions();
          context.handleClose();
        } catch (error) {
          window.console.error(error);
        }
      },
    },
  });
};

const getSessions = () => {
  if(!isFirstLoading.value) {
    isDataLoading.value = true;
  }
  api.getSessions()
    .then((response) => {
      tableData.value = cloneDeep(response.data.payload);
    })
    .catch((error) => {
      errors.value = cloneDeep(error.data.errors) || { message: error.data.message };
    })
    .finally(() => {
      if(!isFirstLoading.value) {
        isDataLoading.value = false;
      } else {
        isFirstLoading.value = false;
      }
    });
};
//#endregion

//#region hooks
onMounted(() => {
  eventBus.$on('handleUpdatePassword', handleUpdatePassword);
  getSessions();
});

onBeforeUnmount(() => {
  eventBus.$off('handleUpdatePassword', handleUpdatePassword);
});
//#endregion
</script>

<template>
  <div class="profile">
    <div class="content-wrapper">
      <UiToolbar>
        <template v-slot:title>
          {{ adminInfo.name }}
        </template>
        <template v-slot:actions>
          <UiButton
            icon="key"
            @click="checkSensitiveAction('handleUpdatePassword')"
          >
            {{ $t('profile_popup.reset_password_title') }}
          </UiButton>
        </template>
      </UiToolbar>
      <div class="t:space-y-24">
        <div class="profile__block">
          <CardTitle
            :title="$t(`user_info.personal_information`)"
          >
            <UiButton
              icon="pen"
              no-border
              @click="handleEditProfile"
            >
              {{ $t('user_info.edit') }}
            </UiButton>
          </CardTitle>
          <div class="t:px-8">
            <CardItem
              no-value
              no-border
            >
              <template v-slot:title>
                <div class="profile__info">
                  <div class="profile__info-name">
                    <UiTooltip
                      :tooltip="adminInfo.name"
                      :disabled="isDisabledTooltipName"
                      placement="bottom"
                    >
                      {{ adminInfo.name }}
                    </UiTooltip>
                  </div>
                  <div class="profile__info-date">
                    <div class="profile__info-date-label">
                      {{ $t('profile.registration_date') }}:
                    </div>
                    {{ $filters.formatDate3(adminInfo.created_at!) }}
                  </div>
                </div>
              </template>
            </CardItem>
            <CardItem no-value>
              <template v-slot:title>
                <div class="t:flex">
                  <UiIcon
                    :size="14"
                    color="var(--text-primary)"
                    name="clock"
                    class="t:mr-4 t:p-1"
                  />
                  {{ profileTimezone }}
                </div>
              </template>
            </CardItem>
            <CardItem no-value>
              <template v-slot:title>
                <div class="t:flex">
                  <UiIcon
                    :size="14"
                    color="var(--text-primary)"
                    name="envelope"
                    class="t:mr-4 t:p-1"
                  />
                  <a
                    :href="`mailto:${adminInfo.email}`"
                    rel="noopener noreferrer"
                    target="_blank"
                  >
                    {{ adminInfo.email }}
                  </a>
                </div>
              </template>
            </CardItem>
            <CardItem no-value>
              <template v-slot:title>
                <UiMessenger
                  :messenger-type="adminInfo.messenger!"
                  :messenger-address="adminInfo.messenger_id!"
                />
              </template>
            </CardItem>
          </div>
        </div>
        <div class="profile__block">
          <CardTitle
            :title="$t('profile_popup.action_unlock_period')"
          >
            <UiButton
              icon="pen"
              no-border
              @click="handleEditSignificantTtl"
            >
              {{ $t('user_info.edit') }}
            </UiButton>
          </CardTitle>
          <div class="t:px-8">
            <CardItem
              :item="{name: significantActionsPeriodName}"
              no-value
              no-border
            />
          </div>
        </div>
        <div class="profile__sessions">
          <div class="t:flex t:justify-between t:items-center t:mb-12">
            <div class="profile__sessions-title">
              {{ $t(`profile.active_sessions`) }}
            </div>
            <UiButton
              type="negative"
              icon="times"
              @click="handleTerminateAll"
            >
              {{ $t('profile.terminate_all') }}
            </UiButton>
          </div>
          <UiTableNew
            v-loading="isDataLoading"
            :fields="fields"
            :data="tableData"
            is-static
            lazy-loading
            disable-sort
            element-loading-custom-class="data-loader-spinner"
            i18n-path="profile.list"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.profile {
  &__sessions-title{
    color: var(--text-body);
    font-size: 20px;
    font-weight: 400;
  }
  &__info-name{
    margin-bottom: 4px;
    color: var(--text-body);
    font-size: 24px;
    font-weight: 500;
  }
  &__info-date {
    display: flex;
    align-items: center;
    font-size: 12px;
    font-weight: 400;
  }
  &__info-date-label {
    margin-right: 2px;
    color: var(--text-body-secondary);
  }
}
</style>
